import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { App } from 'vue';
import { LifecyclePhase } from '../phases';
import type {
  LifecycleHook,
  LifecycleHookPhaseConfig,
  PhaseState,
} from '../types';

const createInitialPhaseState = (): PhaseState => ({
  status: 'idle',
  readyForKey: null,
  error: null,
  runningPromise: null,
});

export const useLifecycleHooksStore = defineStore('lifecycle-hooks', () => {
  let appInstance: App | null = null;

  const phaseStates = ref<Map<LifecyclePhase, PhaseState>>(
    new Map(
      Object.values(LifecyclePhase).map((phase) => [
        phase,
        createInitialPhaseState(),
      ]),
    ),
  );

  const operationsByPhase = new Map<
    LifecyclePhase,
    Array<{ op: LifecycleHook; config: LifecycleHookPhaseConfig }>
  >();

  const registeredNames = new Set<string>();

  const attachApp = (app: App) => {
    appInstance = app;
  };

  const register = (op: LifecycleHook) => {
    if (registeredNames.has(op.name)) return;
    registeredNames.add(op.name);

    for (const phaseConfig of op.phases) {
      const existing = operationsByPhase.get(phaseConfig.phase) ?? [];
      existing.push({ op, config: phaseConfig });
      operationsByPhase.set(phaseConfig.phase, existing);
    }
  };

  const getPhaseState = (phase: LifecyclePhase): PhaseState => {
    let state = phaseStates.value.get(phase);
    if (!state) {
      state = createInitialPhaseState();
      phaseStates.value.set(phase, state);
    }
    return state;
  };

  const run = async (phase: LifecyclePhase, key?: string): Promise<void> => {
    if (!appInstance) {
      throw new Error(
        `[lifecycle-hooks] App not attached. Call lifecycleHooks.attachApp(app) before lifecycleHooks.run().`,
      );
    }

    const state = getPhaseState(phase);

    if (state.status === 'running' && state.runningPromise) {
      return state.runningPromise;
    }

    if (state.status === 'ready' && state.readyForKey === (key ?? null)) {
      return;
    }

    const entries = operationsByPhase.get(phase) ?? [];

    const promise = (async () => {
      state.status = 'running';
      state.error = null;

      // Trigger reactivity
      phaseStates.value = new Map(phaseStates.value);

      const awaitedPromises: Array<{
        promise: Promise<void>;
        critical: boolean;
      }> = [];

      for (const { op, config } of entries) {
        const awaited = config.awaited !== false;
        const critical = config.critical === true;

        const opPromise = Promise.resolve().then(() =>
          appInstance!.runWithContext(() => op.fn()),
        );

        if (awaited) {
          awaitedPromises.push({ promise: opPromise, critical });
        } else {
          // Fire-and-forget: catch to prevent unhandled rejection
          opPromise.catch((err) => {
            console.error(
              `[lifecycle-hooks] Fire-and-forget hook "${op.name}" failed in phase "${phase}":`,
              err,
            );
          });
        }
      }

      const results = await Promise.allSettled(
        awaitedPromises.map((a) => a.promise),
      );

      // If reset(phase) replaced the state mid-run, abandon writes so the
      // fresh state stays idle and the next run() re-executes ops cleanly.
      if (phaseStates.value.get(phase) !== state) return;

      let criticalError: Error | null = null;
      let hasNonCriticalFailure = false;

      for (let i = 0; i < results.length; i++) {
        if (results[i].status === 'rejected') {
          const reason = (results[i] as PromiseRejectedResult).reason;
          if (awaitedPromises[i].critical) {
            criticalError =
              reason instanceof Error ? reason : new Error(String(reason));
          } else {
            hasNonCriticalFailure = true;
            console.warn(
              `[lifecycle-hooks] Non-critical hook failed in phase "${phase}":`,
              reason,
            );
          }
        }
      }

      if (criticalError) {
        state.status = 'error';
        state.error = criticalError;
      } else if (hasNonCriticalFailure) {
        state.status = 'partial';
      } else {
        state.status = 'ready';
      }

      state.readyForKey = key ?? null;
      state.runningPromise = null;

      // Trigger reactivity
      phaseStates.value = new Map(phaseStates.value);

      if (criticalError) {
        throw criticalError;
      }
    })();

    state.runningPromise = promise;
    phaseStates.value = new Map(phaseStates.value);

    return promise;
  };

  const reset = (phase?: LifecyclePhase) => {
    if (phase) {
      phaseStates.value.set(phase, createInitialPhaseState());
    } else {
      for (const p of Object.values(LifecyclePhase)) {
        phaseStates.value.set(p, createInitialPhaseState());
      }
    }
    phaseStates.value = new Map(phaseStates.value);
  };

  return {
    phaseStates,
    attachApp,
    register,
    run,
    reset,
    getPhaseState,
  };
});
